import * as React from 'react';
import {
  ChevronRight,
  Download,
  List,
  Map as MapIcon,
  Package,
  Printer,
  Search,
  Boxes,
  X,
} from 'lucide-react';
import type { StatusPosicao } from '@wms/shared';
import {
  useBusca,
  useCorredores,
  useMapa,
  useMapa3D,
  useSkusPorPosicao,
} from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl, useAtualizarParams } from '@/hooks/useUrlEstado';
import { api } from '@/lib/api';
import { baixarCsv, carimboArquivo } from '@/lib/csv';
import { imprimirEtiquetas } from '@/lib/imprimirEtiquetas';
import { normalizarCorredor } from '@/lib/layout-mapa';
import { ehStatusVisivel, ESTILO_STATUS } from '@/lib/status';
import { cn, compararNatural } from '@/lib/utils';
import type { LinhaLocalizador, RelatorioLocalizadores } from '@/lib/tipos';
import { ChipsFiltro, type Chip } from '@/componentes/ChipsFiltro';
import { DrawerPosicao } from '@/componentes/DrawerPosicao';
import { EtiquetaLocalizador } from '@/componentes/etiqueta/EtiquetaLocalizador';
import { BuscaNaRua } from '@/componentes/mapa/BuscaNaRua';
import { PlantaCorredores } from '@/componentes/mapa/PlantaCorredores';
import { Rack3D } from '@/componentes/mapa/Rack3D';
import { contarStatus, Legenda } from '@/componentes/mapa/Legenda';
import {
  COLUNAS_CSV,
  montarLinhas,
  ROTULO_COLUNA,
  TabelaEstoque,
  VALOR_COLUNA,
  type FiltrosColuna,
  type LinhaEstoque,
} from '@/componentes/tabela/TabelaEstoque';
import type { OpcaoFiltro } from '@/componentes/tabela/FiltroColuna';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Esqueleto } from '@/componentes/ui/primitivos';

/**
 * Estoque — a ferramenta de mercado. Duas visualizações sobre o CD:
 *
 *  MAPA: fluxo de zoom em dois níveis. Sem corredor escolhido, a Planta do CD
 *  (ruas como cartões com heat de ocupação). Escolhida uma rua, a elevação do
 *  rack dela — que na iteração 3 passou a OCUPAR A TELA INTEIRA: o cabeçalho
 *  da página encolhe para uma linha, o rack se ajusta ao espaço disponível e
 *  ainda ganha zoom, painel retrátil e tela cheia.
 *
 *  LISTA: a mesma base em tabela densa, com filtros de status e a gaveta
 *  de detalhe da posição.
 */
export function Estoque(): JSX.Element {
  const { centro, centroAtual } = useContexto();
  const [vista, definirVista] = useParamUrl('vista', 'mapa');
  const [termo, definirTermo] = useParamUrl('q');
  const [statusFiltro, definirStatus] = useParamUrl('status');
  const [corredorParam, definirCorredorParam] = useParamUrl('corredor');
  const [posSelecionada, definirPos] = useParamUrl('pos');
  const atualizarParams = useAtualizarParams();

  /*
   * SKU realçado DENTRO da rua — estado local, separado da busca do topo.
   *
   * A busca do topo pergunta "onde está este item no CD" e vai ao servidor.
   * Dentro da rua a pergunta é "ele está AQUI", e a resposta já veio junto
   * com o mapa do corredor. Guardar em estado próprio evita que procurar na
   * rua mexa no filtro do CD inteiro — e que sair da rua carregue o realce
   * para a planta.
   */
  const [skuNaRua, definirSkuNaRua] = React.useState<number | null>(null);

  /*
   * Localizadores marcados no mapa para a etiqueta. Sobrevivem à troca de
   * rua (dá para marcar na 40, passar para a 41 e imprimir tudo junto); só a
   * troca de centro zera, porque o código é do centro.
   */
  const [marcados, definirMarcados] = React.useState<ReadonlySet<number>>(() => new Set());
  React.useEffect(() => definirMarcados(new Set()), [centro]);
  const alternarMarca = React.useCallback((localizador: number) => {
    definirMarcados((m) => {
      const n = new Set(m);
      if (n.has(localizador)) n.delete(localizador);
      else n.add(localizador);
      return n;
    });
  }, []);

  const emMapa = vista === 'mapa';
  const corredor = corredorParam.trim();
  /** 2º nível do mapa: a rua aberta. É aqui que a tela vira workspace cheio. */
  const emRua = emMapa && corredor !== '';

  const busca = useBusca(centro, termo);
  const corredores = useCorredores(emMapa ? centro : null);
  const mapa3d = useMapa3D(emMapa ? centro : null, corredor || null);
  // A lista usa o mapa completo (1,5 MB); só carrega quando ela está aberta.
  const mapa = useMapa(!emMapa ? centro : null);
  // Os produtos de cada posição: abrem a lista em linhas por SKU (grade e Excel).
  const skus = useSkusPorPosicao(!emMapa ? centro : null);

  // SKU realçado a partir da busca (fecha o ciclo busca → mapa).
  const { skuBuscado, corredoresDoSku } = React.useMemo(() => {
    const dados = busca.data;
    if (!dados || dados.tipoDetectado !== 'sku' || termo.trim().length < 2) {
      return {
        skuBuscado: null as number | null,
        corredoresDoSku: new Map<string, number>(),
      };
    }
    // Um SKU pode estar em MUITAS ruas (ex.: 19722 em 33). Contamos as vagas
    // por rua para o 1º nível destacar e ranquear onde o produto está.
    const contagem = new Map<string, number>();
    for (const p of dados.posicoes) {
      const c = normalizarCorredor(p.corredor);
      contagem.set(c, (contagem.get(c) ?? 0) + 1);
    }
    return { skuBuscado: Number(dados.termo), corredoresDoSku: contagem };
  }, [busca.data, termo]);

  // Trocar de rua zera o realçe local: ele fala de uma rua específica, e
  // carregá-lo para a próxima diria que o item está onde ninguém conferiu.
  React.useEffect(() => {
    definirSkuNaRua(null);
  }, [corredorParam]);

  // Só entra direto na rua quando o SKU está em UMA. Em várias, fica no 1º
  // nível (planta) mostrando todas as ruas que têm o produto — senão as outras
  // somem da vista. É a resposta a "e se o produto estiver em mais de uma rua?".
  React.useEffect(() => {
    if (emMapa && skuBuscado !== null && corredor === '' && corredoresDoSku.size === 1) {
      const unica = [...corredoresDoSku.keys()][0];
      if (unica) definirCorredorParam(unica, { substituir: true });
    }
  }, [emMapa, skuBuscado, corredor, corredoresDoSku, definirCorredorParam]);

  // Sugestões quando a busca é por descrição (não aponta posição direto).
  const produtosSugeridos = React.useMemo(() => {
    const dados = busca.data;
    if (!dados || dados.tipoDetectado !== 'descricao' || termo.trim().length < 2) return [];
    return dados.produtos.slice(0, 12);
  }, [busca.data, termo]);

  // --- Lista: filtros sobre as posições do mapa completo --------------------
  const posicoes = React.useMemo(() => mapa.data?.posicoes ?? [], [mapa.data]);
  const realceLista = React.useMemo(() => {
    const dados = busca.data;
    if (!dados || termo.trim().length < 2) return null;
    if (dados.tipoDetectado === 'sku') return new Set(dados.posicoes.map((p) => p.localizador));
    if (dados.tipoDetectado === 'endereco')
      return new Set(dados.enderecos.map((e) => e.localizador));
    return null;
  }, [busca.data, termo]);

  const filtradasSemStatus = React.useMemo(
    () =>
      posicoes.filter(
        (p) => !realceLista || realceLista.has(p.localizador),
      ),
    [posicoes, realceLista],
  );
  const filtradasAntesColunas = React.useMemo(
    () =>
      statusFiltro === ''
        ? filtradasSemStatus
        : filtradasSemStatus.filter((p) => p.status === statusFiltro),
    [filtradasSemStatus, statusFiltro],
  );

  /*
   * A grade e o Excel são a MESMA lista: uma linha por posição × SKU. Sem os
   * produtos ainda carregados não há linha nenhuma (a grade mostra esqueleto).
   */
  const linhasAntesColunas = React.useMemo(
    () => (skus.data ? montarLinhas(filtradasAntesColunas, skus.data, skuBuscado) : []),
    [filtradasAntesColunas, skus.data, skuBuscado],
  );

  /*
   * Filtro por coluna, estilo Excel. Vive na página, e não na tabela, porque
   * o "Exportar CSV" tem que levar exatamente as linhas que a grade mostra.
   * Fica fora da URL: é recorte de momento, não endereço para compartilhar.
   */
  const [filtrosColuna, definirFiltrosColuna] = React.useState<FiltrosColuna>({});
  React.useEffect(() => definirFiltrosColuna({}), [centro]);
  const passaColunas = React.useCallback(
    (l: LinhaEstoque, ignorar?: string): boolean =>
      Object.entries(filtrosColuna).every(([coluna, valores]) => {
        if (coluna === ignorar) return true;
        const valor = VALOR_COLUNA[coluna];
        return !valor || valores.includes(valor(l));
      }),
    [filtrosColuna],
  );
  const linhas = React.useMemo(
    () => linhasAntesColunas.filter((l) => passaColunas(l)),
    [linhasAntesColunas, passaColunas],
  );
  /** Os valores da coluna entre as linhas que passam nas OUTRAS colunas — como no Excel. */
  const opcoesFiltro = React.useCallback(
    (coluna: string): OpcaoFiltro[] => {
      const valor = VALOR_COLUNA[coluna];
      if (!valor) return [];
      const contagem = new Map<string, number>();
      for (const l of linhasAntesColunas) {
        if (!passaColunas(l, coluna)) continue;
        const v = valor(l);
        contagem.set(v, (contagem.get(v) ?? 0) + 1);
      }
      return [...contagem.entries()]
        .map(([v, qtd]) => ({ valor: v, qtd }))
        .sort((a, b) => compararNatural(a.valor, b.valor));
    },
    [linhasAntesColunas, passaColunas],
  );
  const filtrarColuna = React.useCallback((coluna: string, valores: string[] | null) => {
    definirFiltrosColuna((f) => {
      const n = { ...f };
      if (valores === null) delete n[coluna];
      else n[coluna] = valores;
      return n;
    });
  }, []);
  const contagem = React.useMemo(() => contarStatus(filtradasSemStatus), [filtradasSemStatus]);

  const chips: Chip[] = [];
  if (termo)
    chips.push({
      chave: 'q',
      rotulo: busca.data?.tipoDetectado ?? 'busca',
      valor: termo,
      aoRemover: () => definirTermo(null),
    });
  if (!emMapa && statusFiltro && ehStatusVisivel(statusFiltro))
    chips.push({
      chave: 'status',
      rotulo: 'status',
      valor: ESTILO_STATUS[statusFiltro].rotulo,
      aoRemover: () => definirStatus(null),
    });

  if (!emMapa) {
    for (const [coluna, valores] of Object.entries(filtrosColuna)) {
      chips.push({
        chave: `col-${coluna}`,
        rotulo: ROTULO_COLUNA[coluna] ?? coluna,
        valor: valores.length === 1 ? valores[0] || '(vazio)' : `${valores.length} valores`,
        aoRemover: () => filtrarColuna(coluna, null),
      });
    }
  }

  const localizadorAberto = posSelecionada === '' ? null : Number(posSelecionada);
  const posicaoAberta = posicoes.find((p) => p.localizador === localizadorAberto);
  const alternarStatus = (status: StatusPosicao): void =>
    definirStatus(statusFiltro === status ? null : status);

  // Em ordem FÍSICA (40, 41, 42… / MC001, MC002…), não na ordem de ocupação
  // que a planta usa: "próxima rua" tem que andar pelo corredor de verdade.
  const ruas = React.useMemo(
    () =>
      (corredores.data?.corredores ?? [])
        .map((c) => c.corredor)
        .sort((a, b) => compararNatural(a, b)),
    [corredores.data],
  );

  const seletorVista = (
    <Segmentado
      valor={emMapa ? 'mapa' : 'lista'}
      aoMudar={(v) => definirVista(v)}
      rotuloAcessivel="Visualização"
      tamanho={emRua ? 'pequeno' : 'padrao'}
      opcoes={[
        { valor: 'mapa', rotulo: 'Mapa', icone: <MapIcon className="h-4 w-4" /> },
        { valor: 'lista', rotulo: 'Lista', icone: <List className="h-4 w-4" /> },
      ]}
    />
  );

  return (
    <div className={cn('flex h-full min-h-0 flex-col', emRua ? 'gap-2 p-2 sm:p-3' : 'gap-3 p-3 sm:p-4')}>
      {emRua ? (
        // 2º nível: cabeçalho reduzido a uma linha. Cada pixel economizado aqui
        // é pixel de rack — foi o pedido explícito do cliente.
        <div className="flex items-center gap-2 px-1">
          <nav aria-label="Trilha" className="flex min-w-0 items-center gap-1 text-[12.5px]">
            <span className="text-textoFraco">Estoque</span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-textoSuave" />
            <button
              type="button"
              onClick={() => definirCorredorParam(null)}
              className={acaoTexto}
            >
              Planta do CD
            </button>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-textoSuave" />
            <span className="truncate font-mono font-bold">{corredor}</span>
          </nav>
          {/* O filtro de SKU precisa continuar removível dentro da rua. */}
          <ChipsFiltro chips={chips} />
          <div className="ml-auto flex items-center gap-2">
            {mapa3d.data ? (
              <BuscaNaRua
                vagas={mapa3d.data.vagas}
                sku={skuNaRua}
                aoEscolher={definirSkuNaRua}
              />
            ) : null}
            {seletorVista}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primaria-suave text-primaria ring-1 ring-inset ring-primaria/15">
              <Boxes className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[20px] font-bold leading-tight tracking-[-0.02em]">
                Estoque do CD
              </h1>
              <p className="truncate text-[12.5px] text-textoFraco">
                {centroAtual
                  ? `${centroAtual.centroEstoque} · ${centroAtual.nome}`
                  : 'Selecione um centro'}
              </p>
            </div>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {seletorVista}
            {!emMapa ? (
              <Botao
                variante="secundaria"
                tamanho="pequeno"
                disabled={linhas.length === 0 || centro === null}
                onClick={() => {
                  if (centro !== null) baixarCsv(`estoque-${centro}-${carimboArquivo()}`, linhas, COLUNAS_CSV);
                }}
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </Botao>
            ) : null}
          </div>
        </div>
      )}

      {/* Legenda de status (só na lista) + chips de filtro ativos. */}
      {!emRua && (chips.length > 0 || !emMapa) ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {!emMapa ? (
            <Legenda
              contagem={contagem}
              ativo={ehStatusVisivel(statusFiltro) ? statusFiltro : null}
              aoAlternar={alternarStatus}
            />
          ) : null}
          <ChipsFiltro
            chips={chips}
            aoLimparTudo={() => {
              definirFiltrosColuna({});
              atualizarParams({
                q: null,
                status: null,
                ...(emMapa ? { corredor: null } : {}),
              });
            }}
          />
        </div>
      ) : null}

      {produtosSugeridos.length > 0 && !emRua ? (
        <div className="cartao p-3.5">
          <p className="mb-2.5 flex items-center gap-2 text-[12.5px] text-textoFraco">
            <Search className="h-3.5 w-3.5" />
            Encontramos produtos com esse texto. Escolha um para ver onde ele está:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {produtosSugeridos.map((produto) => (
              <button
                key={produto.produto}
                type="button"
                onClick={() => definirTermo(String(produto.produto))}
                className="flex min-h-[38px] items-center gap-2 rounded-full border border-borda bg-superficie2 px-3.5 text-[12.5px] transition-all hover:-translate-y-0.5 hover:border-primaria hover:text-primaria hover:shadow-xs"
              >
                <Package className="h-3.5 w-3.5" />
                <span className="font-mono font-semibold">{produto.produto}</span>
                <span className="max-w-[220px] truncate text-textoFraco">{produto.descricao}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        {centro === null ? (
          <Esqueleto className="h-full min-h-[420px] w-full" />
        ) : emMapa ? (
          <VistaMapa
            corredor={corredor}
            corredores={corredores}
            mapa3d={mapa3d}
            ruas={ruas}
            skuBuscado={skuBuscado}
            skuNaRua={skuNaRua}
            corredoresDoSku={corredoresDoSku}
            selecionado={localizadorAberto}
            aoAbrirCorredor={(c) => atualizarParams({ corredor: c, pos: null })}
            aoVoltar={() => atualizarParams({ corredor: null, pos: null })}
            aoSelecionar={(loc) => definirPos(loc === null ? null : String(loc))}
            marcados={marcados}
            aoMarcar={alternarMarca}
            acoesMarcados={
              marcados.size > 0 ? (
                <ImprimirMarcados
                  centro={centro}
                  marcados={marcados}
                  aoLimpar={() => definirMarcados(new Set())}
                />
              ) : null
            }
          />
        ) : mapa.isLoading || skus.isLoading ? (
          <Esqueleto className="h-full min-h-[420px] w-full" />
        ) : mapa.isError ? (
          <EstadoErro erro={mapa.error} aoTentarDeNovo={() => void mapa.refetch()} />
        ) : skus.isError ? (
          <EstadoErro erro={skus.error} aoTentarDeNovo={() => void skus.refetch()} />
        ) : posicoes.length === 0 ? (
          <EstadoVazio
            titulo="Este centro não tem posições cadastradas"
            detalhe="Centros sem controle de localizador não aparecem no mapa."
          />
        ) : linhas.length === 0 ? (
          <EstadoVazio
            titulo="Nenhuma posição com esses filtros"
            detalhe="Remova um chip acima para ampliar o resultado."
            icone={<Search className="h-8 w-8" />}
          />
        ) : (
          <TabelaEstoque
            linhas={linhas}
            selecionado={localizadorAberto}
            aoSelecionar={(localizador) => definirPos(String(localizador))}
            filtros={filtrosColuna}
            aoFiltrar={filtrarColuna}
            opcoesFiltro={opcoesFiltro}
          />
        )}
      </div>

      {/* A gaveta de detalhe é do fluxo da lista; no mapa o painel lateral já
          mostra o localizador selecionado. */}
      {!emMapa ? (
        <DrawerPosicao
          localizador={localizadorAberto}
          posicao={posicaoAberta}
          aoFechar={() => definirPos(null)}
        />
      ) : null}
    </div>
  );
}

function VistaMapa({
  corredor,
  corredores,
  mapa3d,
  ruas,
  skuBuscado,
  skuNaRua,
  corredoresDoSku,
  selecionado,
  aoAbrirCorredor,
  aoVoltar,
  aoSelecionar,
  marcados,
  aoMarcar,
  acoesMarcados,
}: {
  corredor: string;
  corredores: ReturnType<typeof useCorredores>;
  mapa3d: ReturnType<typeof useMapa3D>;
  ruas: readonly string[];
  skuBuscado: number | null;
  /** Realçe escolhido dentro da rua; manda sobre o da busca do CD. */
  skuNaRua: number | null;
  corredoresDoSku: Map<string, number>;
  selecionado: number | null;
  aoAbrirCorredor: (corredor: string) => void;
  aoVoltar: () => void;
  aoSelecionar: (localizador: number | null) => void;
  marcados: ReadonlySet<number>;
  aoMarcar: (localizador: number) => void;
  acoesMarcados: React.ReactNode;
}): JSX.Element {
  if (corredor === '') {
    if (corredores.isLoading) return <Esqueleto className="h-full min-h-[420px] w-full" />;
    if (corredores.isError)
      return <EstadoErro erro={corredores.error} aoTentarDeNovo={() => void corredores.refetch()} />;
    const lista = corredores.data?.corredores ?? [];
    if (lista.length === 0) {
      return (
        <EstadoVazio
          titulo="Este centro não tem corredores endereçados"
          detalhe="Centros sem controle de localizador não têm planta para desenhar."
          icone={<MapIcon className="h-8 w-8" />}
        />
      );
    }
    return (
      <div className="h-full min-h-0 overflow-hidden rounded-2xl border border-borda bg-superficie shadow-cartao">
        <PlantaCorredores
          corredores={lista}
          aoAbrirCorredor={aoAbrirCorredor}
          realceSku={skuBuscado}
          vagasPorRua={corredoresDoSku}
        />
      </div>
    );
  }

  if (mapa3d.isLoading) return <Esqueleto className="h-full min-h-[420px] w-full" />;
  if (mapa3d.isError)
    return <EstadoErro erro={mapa3d.error} aoTentarDeNovo={() => void mapa3d.refetch()} />;
  if (!mapa3d.data || mapa3d.data.vagas.length === 0) {
    return (
      <EstadoVazio
        titulo={`Sem vagas desenháveis no corredor ${corredor}`}
        detalhe="Nenhuma posição deste corredor segue o padrão de endereço para montar o rack."
      />
    );
  }
  return (
    <Rack3D
      dados={mapa3d.data}
      realceSku={skuNaRua ?? skuBuscado}
      selecionado={selecionado}
      aoSelecionar={aoSelecionar}
      aoVoltar={aoVoltar}
      ruas={ruas}
      aoTrocarRua={aoAbrirCorredor}
      atualizando={mapa3d.isFetching}
      marcados={marcados}
      aoMarcar={aoMarcar}
      acoesMarcados={acoesMarcados}
    />
  );
}

/**
 * "Imprimir etiquetas (N)" dos localizadores marcados no mapa. É a MESMA
 * etiqueta 100 × 50 do Relatórios › Localizadores, pelo mesmo caminho: os
 * dados (descrição do cadastro e tipo, que o mapa não traz) vêm do mesmo
 * endpoint, filtrado pelos códigos marcados, e saem por `imprimirEtiquetas`.
 */
function ImprimirMarcados({
  centro,
  marcados,
  aoLimpar,
}: {
  centro: number;
  marcados: ReadonlySet<number>;
  aoLimpar: () => void;
}): JSX.Element {
  const [buscando, definirBuscando] = React.useState(false);
  const [imprimindo, definirImprimindo] = React.useState<LinhaLocalizador[] | null>(null);
  const papel = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!imprimindo?.length || !papel.current) return;
    imprimirEtiquetas(papel.current, {
      titulo:
        imprimindo.length === 1
          ? `Etiqueta localizador ${imprimindo[0]!.localizador}`
          : `Etiquetas localizadores (${imprimindo.length})`,
    });
    definirImprimindo(null);
  }, [imprimindo]);

  const imprimir = async (): Promise<void> => {
    definirBuscando(true);
    try {
      const r = await api.get<RelatorioLocalizadores>('/api/relatorios/localizadores', {
        centro,
        localizadores: [...marcados].join(','),
      });
      if (r.linhas.length === 0) window.alert('Nenhum dos localizadores marcados foi encontrado no cadastro.');
      else definirImprimindo(r.linhas);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Não foi possível montar as etiquetas.');
    } finally {
      definirBuscando(false);
    }
  };

  return (
    <>
      <Botao
        variante="suave"
        tamanho="micro"
        carregando={buscando}
        title="Uma etiqueta 100 × 50 por localizador marcado"
        onClick={() => void imprimir()}
      >
        <Printer className="h-3.5 w-3.5" />
        Imprimir etiquetas ({marcados.size})
      </Botao>
      <button
        type="button"
        onClick={aoLimpar}
        title="Desmarcar todos"
        className="flex h-[30px] items-center gap-1 rounded-lg px-1.5 text-[11.5px] font-semibold text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
      >
        <X className="h-3.5 w-3.5" />
        limpar
      </button>
      {imprimindo ? (
        <div ref={papel} className="pointer-events-none absolute left-[-10000px] top-0" aria-hidden>
          {imprimindo.map((l) => (
            <EtiquetaLocalizador
              key={l.localizador}
              dados={{ localizador: l.localizador, descricao: l.descricaoCadastro || l.endereco, tipo: l.tipo }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
